import { useEffect, useRef } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/pages/home/components/Footer';

const pipelineItems = [
  {
    phase: 'Commercial',
    status: 'active',
    title: 'Depression Risk Assessment',
    description: 'World\'s first regulatory approved SaMD for depression risk assessment via voice.',
    timeline: 'Live',
  },
  {
    phase: 'Clinical Trials',
    status: 'active',
    title: 'Bipolar Disorder Screening',
    description: 'Voice-based screening for bipolar disorder with 87% accuracy in preliminary trials.',
    timeline: '2026',
  },
  {
    phase: 'Clinical Trials',
    status: 'active',
    title: 'Alzheimer\'s Early Detection',
    description: 'Acoustic feature analysis for pre-symptomatic dementia prediction.',
    timeline: '2026-2027',
  },
  {
    phase: 'Research',
    status: 'upcoming',
    title: 'Parkinson\'s Disease Monitoring',
    description: 'Continuous voice monitoring for early-stage Parkinson\'s detection.',
    timeline: '2027',
  },
  {
    phase: 'Research',
    status: 'upcoming',
    title: 'COPD & Asthma Monitoring',
    description: 'Respiratory health assessment through cough and breathing pattern analysis.',
    timeline: '2027',
  },
  {
    phase: 'Research',
    status: 'upcoming',
    title: 'Heart Failure Detection',
    description: 'Cardiovascular biomarker detection via speech and vocal cord analysis.',
    timeline: '2027-2028',
  },
  {
    phase: 'Discovery',
    status: 'upcoming',
    title: 'Diabetes Screening',
    description: 'Type 2 diabetes prediction through voice biomarker analysis.',
    timeline: '2028',
  },
  {
    phase: 'Discovery',
    status: 'upcoming',
    title: 'Sleep Disorder Assessment',
    description: 'Sleep apnea and insomnia detection through overnight voice monitoring.',
    timeline: '2028',
  },
];

export default function PipelinePage() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in-up');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <main className="relative min-h-screen bg-background-50">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 px-6 md:px-12 lg:px-20">
        <div className="max-w-7xl mx-auto text-center">
          <div className="reveal opacity-0 mb-4">
            <span className="font-body text-xs font-semibold tracking-widest text-primary-500 uppercase">
              Pipeline
            </span>
          </div>
          <h1 className="reveal opacity-0 font-display text-4xl md:text-5xl lg:text-6xl font-medium text-foreground-50 leading-tight mb-6">
            We started with mental health,
            <br />
            <span className="text-primary-500">the potential is limitless</span>
          </h1>
          <p className="reveal opacity-0 font-body text-base text-foreground-400 max-w-2xl mx-auto">
            Our voice AI technology roadmap spans 14 medical specialties, from regulatory-approved products to cutting-edge research.
          </p>
        </div>
      </section>

      {/* Timeline */}
      <section ref={sectionRef} className="relative pb-20 md:pb-32 px-6 md:px-12 lg:px-20">
        <div className="max-w-4xl mx-auto">
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-4 md:left-8 top-0 bottom-0 w-px bg-background-200/70" />

            {pipelineItems.map((item, i) => {
              const statusColor = item.status === 'active'
                ? 'bg-primary-500'
                : 'bg-accent-500';

              const phaseColor = item.status === 'active'
                ? 'text-primary-500 bg-primary-500/10'
                : 'text-accent-500 bg-accent-500/10';

              return (
                <div
                  key={i}
                  className="reveal opacity-0 relative pl-12 md:pl-20 pb-12 last:pb-0"
                >
                  {/* Dot */}
                  <div className={`absolute left-2 md:left-6 top-2 w-5 h-5 rounded-full ${statusColor} border-4 border-background-50`} />

                  <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-6">
                    <div className="shrink-0">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full font-body text-xs font-medium ${phaseColor}`}>
                        {item.phase}
                      </span>
                      <span className="block font-body text-xs text-foreground-600 mt-2">
                        {item.timeline}
                      </span>
                    </div>
                    <div className="flex-1 p-5 md:p-6 rounded-xl bg-background-100 border border-background-200/70 hover:border-background-300/60 transition-all duration-500">
                      <h3 className="font-body text-base font-semibold text-foreground-50 mb-2">
                        {item.title}
                      </h3>
                      <p className="font-body text-sm text-foreground-400 leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}